import {
  type ClassAttributes,
  Fragment,
  type HTMLAttributes,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Observer, observer } from "mobx-react-lite";
import { usePrevious } from "react-use";
import { useSimpleTabsStore } from "./SimpleTabsV2.shared.ts";
import { SimpleTabsProvider } from "./SimpleTabsV2.shared.ts";
import * as styles from "./SimpleTabsV2.styles.tsx";
import type {
  Tab as TabModel,
  TabOptions,
  TabsStore,
} from "src/lib/tabs-ui/TabsStore.tsx";
import { simpleTabsV2Classes } from "src/components/SimpleTabsV2/SimpleTabsV2.constants.ts";
import { TabsDropdown } from "src/components/SimpleTabsV2/SimpleTabsV2Dropdown.tsx";
import { TabsDropdownTrigger } from "src/components/SimpleTabsV2/SimpleTabsV2DropdownTrigger.tsx";
import { useInView } from "react-intersection-observer";
import { composeRefs } from "@exonn/hooks";
import { TooltipV3 } from "src/components/TooltipV3/TooltipV3.tsx";
import { Flex } from "src/components/Flex.tsx";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";
import { Text } from "src/components/Text/Text.tsx";

export type SimpleTabsV2Props = {
  store: TabsStore;
  tabs?: TabOptions[];
  activeTabId?: string;
  defaultActiveTabId?: string;
  hasControlledActiveTabId?: boolean;
  additionalElements?: ReactNode[];
  onPlusButtonClick?: () => void;
  showPlusButton?: boolean;
  disablePlusButton?: boolean;
  onActiveTabIdChange?: (id: string | undefined) => void;
  stickyHeadersEnabled?: boolean;
  plusButtonHelperText?: ReactNode;
  headerProps?: HTMLAttributes<HTMLDivElement>;
  onTabsChange?: (nextTabs: TabOptions[], closedTabs: TabOptions[]) => void;
} & HTMLAttributes<HTMLDivElement>;

export const SimpleTabsV2 = observer(function SimpleTabsObserved(
  props: SimpleTabsV2Props,
) {
  const {
    store,
    tabs,
    activeTabId,
    headerProps,
    hasControlledActiveTabId = false,
    disablePlusButton = false,
    showPlusButton = false,
    plusButtonHelperText,
    onPlusButtonClick,
    defaultActiveTabId,
    additionalElements,
    onActiveTabIdChange,
    onTabsChange,
    ...rest
  } = props;
  store.setHasControlledTabId(hasControlledActiveTabId);

  useEffect(() => {
    if (hasControlledActiveTabId && activeTabId !== store.activeTabId) {
      store.setActiveTabId(activeTabId, {
        emitEvents: false,
      });
    }
  }, [hasControlledActiveTabId, activeTabId, store]);

  const previousTabs = usePrevious(tabs);

  useEffect(() => {
    if (tabs && previousTabs !== tabs) {
      const nextTabs = tabs.map((options) =>
        store.createTab({
          isClosable: false,
          ...options,
        }),
      );
      store.setTabs(nextTabs, {
        emitEvents: false,
      });
    }
  }, [tabs, previousTabs, store]);

  useEffect(() => {
    return store.emitter.on("tabsSet", (nextTabs, closedTabs) => {
      onTabsChange?.(nextTabs, closedTabs);
    });
  }, [onTabsChange, store, onActiveTabIdChange]);

  useEffect(() => {
    return store.emitter.on("activeTabChange", (tab) => {
      onActiveTabIdChange?.(tab?.id);
    });
  }, [onActiveTabIdChange, store]);

  useEffect(
    () => {
      if (defaultActiveTabId) {
        store.setActiveTabId(defaultActiveTabId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <SimpleTabsProvider store={store}>
      <div className={simpleTabsV2Classes.root} css={styles.root} {...rest}>
        <TabHeadersList
          {...headerProps}
          disablePlusButton={disablePlusButton}
          showPlusButton={showPlusButton}
          additionalElements={additionalElements}
          onPlusButtonClick={onPlusButtonClick}
          plusButtonHelperText={plusButtonHelperText}
        />
        <TabsContent />
      </div>
    </SimpleTabsProvider>
  );
});

const TabsContent = observer(() => {
  const store = useSimpleTabsStore();
  const activeTab = store.activeTab;
  if (!activeTab) {
    return null;
  }
  return (
    <div className={simpleTabsV2Classes.content} css={styles.content}>
      {activeTab.content}
    </div>
  );
});

const TabHeadersList = observer(
  ({
    additionalElements,
    showPlusButton,
    plusButtonHelperText,
    disablePlusButton,
    onPlusButtonClick,
    ...rest
  }: {
    additionalElements?: ReactNode[];
    showPlusButton: boolean;
    plusButtonHelperText?: ReactNode;
    disablePlusButton?: boolean;
    onPlusButtonClick?: () => void;
  } & HTMLAttributes<HTMLDivElement>) => {
    const store = useSimpleTabsStore();

    return (
      <div
        {...rest}
        className={simpleTabsV2Classes.headers}
        css={styles.tabsContainer()}
      >
        <div
          css={styles.tabsWrapper}
          ref={(node) => store.setContainerEl(node)}
        >
          {store.tabs.flatMap((tab) =>
            !tab.isHidden ? (
              <Tab showPlusButton={showPlusButton} key={tab.id} tab={tab} />
            ) : (
              []
            ),
          )}

          {additionalElements && (
            <div css={styles.addElements}>
              {additionalElements.map((elem, i) => (
                <Fragment key={i}>{elem}</Fragment>
              ))}
            </div>
          )}
        </div>
        {showPlusButton && (
          <>
            <Flex css={styles.plusButtonWrapper}>
              <TooltipV2
                disabled={!plusButtonHelperText || disablePlusButton}
                delay={750}
                content={plusButtonHelperText}
              >
                <PlusButton
                  disabled={disablePlusButton}
                  onClick={onPlusButtonClick}
                />
              </TooltipV2>
            </Flex>
          </>
        )}
        <TabsDropdown>
          <Observer>
            {() => (
              <TabsDropdownTrigger
                style={{
                  visibility:
                    store.outOfViewTabs.length > 0 ? "visible" : "hidden",
                }}
              />
            )}
          </Observer>
        </TabsDropdown>
      </div>
    );
  },
);

const Tab = observer(function TabObserved({
  tab,
  showPlusButton,
}: {
  tab: TabModel;
  showPlusButton: boolean;
}) {
  const store = useSimpleTabsStore();
  const localRef = useRef<HTMLButtonElement | null>(null);

  const hideSeparator =
    tab.isActive ||
    tab.next?.isHovered ||
    tab.next?.isActive ||
    (tab.isLast && !showPlusButton);

  const handleClose: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();

    tab.close();
  };

  const handleAuxClick = (e: MouseEvent) => {
    if (e.button !== 1) {
      return;
    }
    // middle click (wheel click)
    if (!tab.isClosable) {
      return;
    }

    tab.close();
  };

  const isClosable = tab.isClosable;
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { ref: connectIntersectionObserver } = useInView({
    root: store.containerEl,
    threshold: 0.99,
    onChange: (inView, entry) => {
      if (!store.containerEl?.contains(localRef.current)) {
        return;
      }
      tab.setIsOutOfView(!inView);
    },
  });

  const disableTooltipEnabled = tab.isDisabled && !!tab.disabledTooltip;

  const handleTooltipOpen = (value: boolean) => {
    if (disableTooltipEnabled) {
      setTooltipOpen(value);
    }
  };

  return (
    <TooltipV3
      open={tooltipOpen && disableTooltipEnabled}
      onOpenChange={handleTooltipOpen}
      content={tab.disabledTooltip}
    >
      <button
        disabled={tab.isDisabled}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          tab.activate({
            emitEvents: true,
            updateState: !store.hasControlledTabId,
          });
        }}
        ref={composeRefs(
          (node) => (localRef.current = node),
          (node) => connectIntersectionObserver(node),
        )}
        onAuxClick={handleAuxClick}
        onPointerEnter={() => store.setHoveredTabId(tab.id)}
        onPointerLeave={() => store.setHoveredTabId(undefined)}
        css={styles.item({
          isActive: tab.isActive,
          showIndicator: tab.showIndicator,
          isClosable: isClosable,
        })}
      >
        <div className={"tab-title"} css={styles.tabTitle}>
          {tab.title}
        </div>
        <span
          css={styles.unsavedChangesIndicator({
            visible: tab.hasUnsavedChanges,
          })}
        />
        {tab.isClosable && (
          <span
            onMouseDown={(e) => {
              // prevents the tab from being activated when the close button is clicked
              e.preventDefault();
            }}
            css={styles.closeButton}
            className="tab-close-icon"
            onClick={handleClose}
          >
            <i className={"icon-fl-15-close"} />
          </span>
        )}

        {!hideSeparator && (
          <div className={"tab-separator"} css={styles.separator} />
        )}
      </button>
    </TooltipV3>
  );
});

const PlusButton = ({
  disabled = false,
  ...restProps
}: {
  disabled?: boolean;
} & HTMLAttributes<HTMLButtonElement> &
  ClassAttributes<HTMLButtonElement>) => {
  return (
    <button
      css={styles.plusButton({
        disabled,
      })}
      {...restProps}
    >
      {/* @ts-ignore: typescript doesn't see nested colors */}
      <Text css={styles.plusButtonText} variant="body2">
        <i className={"icon-15-add"} css={styles.plusButtonIcon} />
      </Text>
    </button>
  );
};
